import React, { createContext, useContext, useEffect, useState } from 'react';
import { jwtDecode } from 'jwt-decode';
import Cookies from 'js-cookie';

interface AuthContextType {
    user: { email: string; role: string[], id: string } | null;
    isAuthenticated: boolean;
    login: (token: string) => void;
    logout: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [user, setUser] = useState<{ email: string; role: string[], id: string } | null>(null);
    const [isAuthenticated, setIsAuthenticated] = useState(false);

    const validateToken = (token: string): boolean => {
        try {
            const decoded: any = jwtDecode(token);
            const currentTime = Date.now() / 1000;
            return decoded.exp > currentTime;
        } catch (error) {
            return false;
        }
    };

    const login = (token: string) => {
        // Store token in cookies with 30 days expiration
        Cookies.set('accessToken', token, { 
            expires: 30, // 30 days
            secure: true,
            sameSite: 'strict'
        });

        const decoded: any = jwtDecode(token);
        setUser({ email: decoded.email, role: decoded.role, id: decoded.sub });
        setIsAuthenticated(true);
    };

    const logout = () => {
        Cookies.remove('accessToken');
        setUser(null);
        setIsAuthenticated(false);
    };

    useEffect(() => {
        const token = Cookies.get('accessToken');
        if (token && validateToken(token)) {
            const decoded: any = jwtDecode(token);
            setUser({ email: decoded.email, role: decoded.role, id: decoded.sub });
            setIsAuthenticated(true);
        } else {
            // Token is invalid or expired
            logout();
        }
    }, []);

    return (
        <AuthContext.Provider value={{ user, isAuthenticated, login, logout }}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (context === undefined) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};
